// src/Screens/PublicRoomsContent.jsx
// Extracted from PublicRooms.jsx so the Rooms hub (RoomsHub.jsx) and the
// standalone /public-rooms route can share the same content without
// duplicating its data-fetching/rendering logic. PublicRooms.jsx is now a
// thin wrapper around this for the standalone deep link.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, RefreshCw, Users, Globe, Info, ArrowLeft } from 'lucide-react';
import publicRoomsService from '../Services/publicRoomService';
import joinRoomService from '../Services/joinRoomService';
import useProfileStore from '../stores/profileStore';

// Country flag helper function
const getCountryFlag = (countryName) => {
  const countryToFlag = {
    Nigeria: '🇳🇬',
    'United States': '🇺🇸',
    'United Kingdom': '🇬🇧',
    Canada: '🇨🇦',
    Australia: '🇦🇺',
    Germany: '🇩🇪',
    France: '🇫🇷',
    India: '🇮🇳',
    Brazil: '🇧🇷',
    Mexico: '🇲🇽',
    'South Africa': '🇿🇦',
    Kenya: '🇰🇪',
    Ghana: '🇬🇭',
    Egypt: '🇪🇬',
    Morocco: '🇲🇦',
    Singapore: '🇸🇬',
    Malaysia: '🇲🇾',
    Thailand: '🇹🇭',
    Philippines: '🇵🇭',
    Indonesia: '🇮🇩',
    Vietnam: '🇻🇳',
    Japan: '🇯🇵',
    'South Korea': '🇰🇷',
    China: '🇨🇳',
    Italy: '🇮🇹',
    Spain: '🇪🇸',
    Portugal: '🇵🇹',
    Netherlands: '🇳🇱',
    Belgium: '🇧🇪',
    Switzerland: '🇨🇭',
    Austria: '🇦🇹',
    Sweden: '🇸🇪',
    Norway: '🇳🇴',
    Denmark: '🇩🇰',
    Finland: '🇫🇮',
    Ireland: '🇮🇪',
    Poland: '🇵🇱',
    'Czech Republic': '🇨🇿',
    Hungary: '🇭🇺',
    Romania: '🇷🇴',
    Bulgaria: '🇧🇬',
    Greece: '🇬🇷',
    Turkey: '🇹🇷',
    Russia: '🇷🇺',
    Ukraine: '🇺🇦',
    Argentina: '🇦🇷',
    Chile: '🇨🇱',
    Colombia: '🇨🇴',
    Peru: '🇵🇪',
    Venezuela: '🇻🇪',
    Uruguay: '🇺🇾',
    Paraguay: '🇵🇾',
    Bolivia: '🇧🇴',
    Ecuador: '🇪🇨',
  };

  return countryToFlag[countryName] || '🌍';
};

// Info Modal Component
const InfoModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center">
              <Info className="h-6 w-6 text-red-600 mr-2" />
              <h2 className="text-2xl font-bold text-gray-800">About Public Rooms</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="space-y-4 text-gray-700">
            <p>
              Public rooms are games created by other players that anyone can discover and join.
              Perfect for when you don't have friends available to play with!
            </p>

            <div>
              <h3 className="font-semibold mb-2">How it works:</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Browse rooms created by other players</li>
                <li>Join any room that has available spots</li>
                {/* <li>Filter by country or player count</li>
                <li>Fresh rooms (under 15 minutes) appear first</li> */}
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Creating public rooms:</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Switch on "Make room public" on Home before you create a room</li>
                <li>Your room will appear in this list for others to find</li>
                {/* <li>Rooms auto-hide after 15 minutes if no one joins</li> */}
              </ul>
            </div>
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={onClose}
              className="bg-red-600 text-white font-semibold px-6 py-2 rounded-lg hover:bg-red-700 transition duration-300"
            >
              Got it!
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function PublicRoomsContent({ onBack }) {
  const [rooms, setRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isJoining, setIsJoining] = useState(null);
  const [totalAvailable, setTotalAvailable] = useState(0);
  const [availableCountries, setAvailableCountries] = useState(['Nigeria']);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [playerCountFilter, setPlayerCountFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const navigate = useNavigate();

  // Load rooms on component mount
  useEffect(() => {
    loadPublicRooms();
  }, []);

  const loadPublicRooms = async () => {
    try {
      setIsLoading(true);

      const filters = {
        country: selectedCountry === 'all' ? undefined : selectedCountry,
        playerCountFilter,
        searchQuery: searchQuery.trim() || undefined,
      };

      const [roomsResult, countries] = await Promise.all([
        publicRoomsService.getPublicRooms(filters),
        publicRoomsService.getAvailableCountries(),
      ]);

      if (roomsResult.success) {
        setRooms(roomsResult.rooms);
        setTotalAvailable(roomsResult.totalAvailable || 0);
      } else {
        alert('Error: ' + roomsResult.message);
      }

      setAvailableCountries(['all', ...countries]);
    } catch (error) {
      console.error('Error loading public rooms:', error);
      alert('Failed to load public rooms. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      await loadPublicRooms();
    } catch (error) {
      console.error('Error refreshing:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleReshuffle = async () => {
    try {
      const filters = {
        country: selectedCountry === 'all' ? undefined : selectedCountry,
        playerCountFilter,
        searchQuery: searchQuery.trim() || undefined,
      };

      const result = await publicRoomsService.reshuffleRooms(filters);

      if (result.success) {
        setRooms(result.rooms);
        setTotalAvailable(result.totalAvailable || 0);
      } else {
        alert('Error: ' + result.message);
      }
    } catch (error) {
      console.error('Error reshuffling:', error);
      alert('Failed to reshuffle rooms. Please try again.');
    }
  };

  const handleJoinRoom = async (room) => {
    if (isJoining) return;

    try {
      setIsJoining(room.roomId);

      // First check if user is already in this room
      const userRoomStatus = await joinRoomService.getUserRoomStatus(room.roomId);

      if (userRoomStatus.lookupFailed) {
        // Can't tell whether we're already in this room, so don't try to join it
        alert(userRoomStatus.message || 'Failed to join room. Please try again.');
        return;
      }

      if (userRoomStatus.isInRoom) {
        // User is already in the room, navigate to create-room (lobby) screen
        console.log('User already in room, navigating to lobby');
        navigate('/create-room');
        return;
      }

      // The saved name (this device's or the account's), or ask for one
      await useProfileStore.getState().loadDisplayName();
      const playerName = useProfileStore.getState().displayName;

      if (!playerName?.trim()) {
        // Navigate to join room screen if no name available
        navigate(`/join-room/${room.roomId}`);
        return;
      }

      // Store public room join tracking
      localStorage.setItem('lastJoinWasPublic', 'true');
      localStorage.setItem('publicRoomHostName', room.hostName);

      // Try direct join with existing name
      const result = await joinRoomService.joinRoom(room.roomId, playerName.trim(), 'public_browse');

      if (result.success) {
        // Successfully joined, navigate to lobby
        useProfileStore.getState().syncToAccount();
        navigate('/create-room');
      } else {
        // Join failed (name taken, room full, etc.), go to join screen for user input
        navigate(`/join-room/${room.roomId}`);
      }

    } catch (error) {
      console.error('Error joining room:', error);
      alert('Failed to join room. Please try again.');
    } finally {
      setIsJoining(null);
    }
  };

  const applyFilters = () => {
    loadPublicRooms();
    setShowFilters(false);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCountry('all');
    setPlayerCountFilter('all');
    setShowFilters(false);
    setTimeout(loadPublicRooms, 100);
  };

  const getPlayerCountColor = (current, max) => {
    const percentage = current / max;
    if (percentage < 0.5) return 'text-green-600';
    if (percentage < 0.8) return 'text-yellow-600';
    return 'text-red-600';
  };

  // With nothing to browse, search, filters and "Showing 0 of 0" are noise.
  const filtersActive =
    !!searchQuery.trim() || selectedCountry !== 'all' || playerCountFilter !== 'all';
  const showBrowseTools = totalAvailable > 0 || filtersActive;

  if (isLoading && !isRefreshing) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading public rooms...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        {onBack && (
          <button
            onClick={onBack}
            aria-label="Back to home"
            className="-ml-2 mr-1 w-11 h-11 flex items-center justify-center rounded-full text-brand-text hover:bg-brand-accentMuted transition-colors flex-shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}
        <div className="flex-1">
          <h1 className="text-lg font-bold text-brand-text">Find a public game</h1>
          <p className="text-xs text-brand-textSecondary mt-0.5">Join a room someone else made public</p>
        </div>

        <button
          onClick={() => setShowInfoModal(true)}
          aria-label="About public rooms"
          className="ml-3 w-11 h-11 flex items-center justify-center bg-brand-accentMuted text-brand-accent rounded-full hover:bg-red-100 transition duration-300 flex-shrink-0"
        >
          <Info className="h-4 w-4" />
        </button>
      </div>

      {/* Search and Filter Row */}
      {showBrowseTools && (
      <div className="flex gap-2 mb-4">
        <div className="flex-1 flex items-center border border-brand-border rounded-lg bg-brand-surface px-3 py-2.5">
          <Search className="h-4 w-4 text-brand-placeholder mr-2 flex-shrink-0" />
          <input
            type="text"
            className="flex-1 outline-none text-sm placeholder-brand-placeholder min-w-0"
            placeholder="Search room names..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && loadPublicRooms()}
          />
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          aria-label="Filters"
          aria-expanded={showFilters}
          className="flex items-center gap-1.5 px-3 py-2.5 bg-brand-accent text-white rounded-lg hover:bg-red-700 transition duration-300 text-sm font-medium"
        >
          <Filter className="h-4 w-4" />
          <span className="hidden sm:inline">Filters</span>
        </button>
      </div>
      )}

      {/* Filter Options */}
      {showFilters && (
        <div className="bg-brand-surface rounded-lg p-3.5 mb-4 border border-brand-border">
          {/* Player Count Filter */}
          <div className="mb-3.5">
            <h3 className="text-xs font-semibold text-brand-textSecondary mb-2">Player Count</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'all', label: 'All' },
                { key: 'less-than-3', label: 'Less than 3' },
                { key: 'more-than-3', label: 'More than 3' },
              ].map((option) => (
                <button
                  key={option.key}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition duration-300 ${
                    playerCountFilter === option.key
                      ? 'bg-brand-accent text-white'
                      : 'border border-brand-border text-brand-text hover:bg-brand-accentMuted'
                  }`}
                  onClick={() => setPlayerCountFilter(option.key)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Filter Actions */}
          <div className="flex gap-2">
            <button
              onClick={applyFilters}
              className="flex-1 py-2 bg-brand-accent text-white rounded-lg hover:bg-red-700 transition duration-300 text-sm font-medium"
            >
              Apply
            </button>
            <button
              onClick={clearFilters}
              className="px-4 py-2 border border-brand-border text-brand-text rounded-lg hover:bg-brand-accentMuted transition duration-300 text-sm"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Room Stats */}
      {showBrowseTools && (
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-brand-textSecondary">
          Showing {rooms.length} of {totalAvailable} rooms
        </p>
        {totalAvailable > 15 && (
          <button
            onClick={handleReshuffle}
            className="flex items-center px-2.5 py-1 bg-brand-accentMuted text-brand-accent rounded-full hover:bg-red-100 transition duration-300"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
            <span className="text-[11px] font-medium">Shuffle</span>
          </button>
        )}
      </div>
      )}

      {/* Rooms List */}
      <div className="space-y-2.5">
        {rooms.length === 0 ? (
          <div className="text-center py-16">
            <Globe className="h-10 w-10 text-brand-placeholder mx-auto mb-3" />
            <h2 className="text-sm font-semibold text-brand-text mb-1">
              {filtersActive ? 'No rooms match those filters' : 'No public games right now'}
            </h2>
            <p className="text-xs text-brand-textSecondary mb-5 px-4">
              {filtersActive ? 'Try clearing them, or start one yourself.' : 'Start one and others can join you.'}
            </p>
            <button
              onClick={() => navigate('/', { state: { publicRoom: true } })}
              className="px-5 py-2.5 bg-brand-accent text-white rounded-lg hover:bg-red-700 transition duration-300 text-sm font-medium"
            >
              Create Public Room
            </button>
          </div>
        ) : (
          rooms.map((room) => (
            <div
              key={room.roomId}
              className="bg-brand-surface rounded-lg p-3.5 border border-brand-border shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
            >
              {/* Room Header */}
              <div className="flex items-start justify-between mb-2.5">
                <div className="flex-1 mr-3 min-w-0">
                  <h3 className="text-sm font-bold text-brand-text leading-tight truncate">
                    {room.roomName}
                  </h3>
                  <p className="text-xs text-brand-textSecondary mt-0.5">
                    Host: {room.hostName}
                  </p>
                </div>

                {/* Player Count Badge */}
                <div
                  className={`px-2 py-0.5 rounded-full text-[11px] font-bold flex-shrink-0 ${
                    room.playerCount >= room.maxPlayers
                      ? 'bg-red-100 text-red-700'
                      : room.playerCount >= 3
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-green-100 text-green-700'
                  }`}
                >
                  {room.playerCount}/{room.maxPlayers}
                </div>
              </div>

              {/* Categories Row */}
              <div className="flex gap-1.5 overflow-x-auto pb-0.5 mb-3">
                {room.categories.slice(0, 4).map((category) => (
                  <span
                    key={category}
                    className="px-2 py-0.5 bg-brand-accentMuted text-brand-accent text-[11px] font-medium rounded-md whitespace-nowrap"
                  >
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </span>
                ))}
                {room.categories.length > 4 && (
                  <span className="px-2 py-0.5 bg-brand-input text-brand-textSecondary text-[11px] font-medium rounded-md whitespace-nowrap">
                    +{room.categories.length - 4}
                  </span>
                )}
              </div>

              {/* Join Button */}
              <button
                onClick={() => handleJoinRoom(room)}
                disabled={room.playerCount >= room.maxPlayers || isJoining === room.roomId}
                className={`w-full py-2.5 rounded-lg text-sm font-semibold transition duration-300 ${
                  room.playerCount >= room.maxPlayers || isJoining === room.roomId
                    ? 'bg-brand-input text-brand-placeholder cursor-not-allowed'
                    : 'bg-brand-accent text-white hover:bg-red-700'
                }`}
              >
                {isJoining === room.roomId ? (
                  <span className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500 mr-2"></div>
                    Joining...
                  </span>
                ) : room.playerCount >= room.maxPlayers ? (
                  'Room Full'
                ) : (
                  'Join Game'
                )}
              </button>
            </div>
          ))
        )}
      </div>

      {/* Refresh Button */}
      <div className="text-center mt-5">
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="px-5 py-2.5 bg-brand-surface border border-brand-accent text-brand-accent rounded-lg hover:bg-brand-accentMuted transition duration-300 disabled:opacity-50 text-sm font-medium"
        >
          {isRefreshing ? (
            <span className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></div>
              Refreshing...
            </span>
          ) : (
            'Refresh Rooms'
          )}
        </button>
      </div>

      {/* Info Modal */}
      <InfoModal isOpen={showInfoModal} onClose={() => setShowInfoModal(false)} />
    </div>
  );
}
